import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Button from '../Button/Button';
import Input from '../Input/Input';
import createFakeEvent from '../../lib/createFakeEvent';
import datasetFromProps from '../../lib/datasetFromProps';
import { isNil, isObject, isString } from '../../lib/utils';
import { valueEqualsOption } from '../SelectList/util';

export const caseInsensitiveMatch = (option, searchValue) =>
  option.toLowerCase().includes(searchValue.toLowerCase())

export const getSearchOptions = (options, searchValue) => {
  if (isNil(searchValue) || searchValue === '') {
    return options;
  }
  return options.filter(option => {
    if (isString(option)) {
      return caseInsensitiveMatch(option, searchValue);
    } else if (isObject(option)) {
      const keys = Object.keys(option);
      return keys.some(key => isString(option[key]) && caseInsensitiveMatch(option[key], searchValue));
    }
    return false;
  });
};

/**
 * The `Select` component represents a control that provides a dropdown of options. Only one option can be selected from the
 * dropdown. Use {@link TagInput} instead if you need support for selecting multiple options.
 *
 * The Select component is built on top of the {@link Input} component and supports all of its features. It adds an arrow
 * button to the right of the input field to toggle the dropdown. The dropdown will display the items in your *options* prop.
 * The options can either be a string of arrays or objects. If objects are provided, the component will use the *labelKey*
 * and *valueKey* props to get the label and value from the options.
 *
 * Input field will be editable if *readOnly* is set to `false`. This will also enable the input field to be used to search
 * for options. An option will be shown if the input value appears anywhere in the option label.
 *
 * @property {string} autoComplete If set to "off", all auto complete in the browser will be turned off. Defaults to
 * "off" if the component has *options* or a custom *dropdown*. Otherwise it defaults to "on".
 * @property {string} className The class name of this component. Will be appended to "core-select". This component extends
 * the {@link Input} which will add additional classes.
 * @property {boolean} clearable If set to `true`, the user will be able to clear the selected option. Defaults to `false`.
 * @property {boolean} disabled The component will be disabled if this prop is set to `true`. Defaults to `false`.
 * @property {node} dropdown Sets a custom dropdown component. This can be used to e.g. render a {@link Tree} component as
 * the dropdown instead of the standard list dropdown.
 * @property {node} errorMessage An error message that will be shown below the input field. Defaults to no error message.
 * @property {string} iconName Custom icon that will be displayed to the right most edge of the component. The icon name can
 * be any of the embedded icons in {@link Icon}.
 * @property {string} label The label of the Select component. Defaults to no label.
 * @property {string} labelKey The key to used to find the display value if item is an object. Defaults to "label".
 * @property {string} nestedKey The name of the property of your children array for each option object. Setting this prop
 * will result in the *SelectList* displaying a tree structure.
 * @property {string} nestedKeySelectable A named property in your objects in *options* which determines whether or not the
 * option is selectable or not. Defaults to `undefined`.
 * @property {function} onChange The onChange listener that will be called whenever the user selects a value from the
 * dropdown.
 * @property {function} onUnlock A callback that is called whenever the user has clicked on the lock icon. You can use this
 * to unlock all locked fields in a form.
 * @property {array} options An array of strings or objects that will be displayed in the dropdown. Supports both array of
 * strings and objects. See *labelKey* and *valueKey*.
 * @property {string} placeholder The placeholder text for this component. Defaults to "Select an option...".
 * @property {boolean} readOnly If set to `false`, the user will be able to manually type in options in the field. Defaults
 * to `true` which only allows options to be selected from a dropdown.
 * @property {boolean} required If set to `true`, a required indicator will be displayed for the component. Defaults to
 * `false`.
 * @property {boolean} showSearch If set to `true`, a Search icon is displayed to the left of the input field. No search icon
 * is displayed by default.
 * @property {boolean} sortOptions If `true`, the options will be sorted alphabetically by label or value. Defaults to
 * `true`. **Note:** Sorting is currently not supported for nested values.
 * @property {string|number|object} value The selected value. Supports strings, numbers and objects. If the type is an
 * object the object's `label` attribute will be used as value. You can override this using the *labelKey* prop.
 * @property {string} valueKey The key to search in if item is an object. Defaults to "value".
 * @property {node} warningMessage A warning message that will be shown below the component. Defaults to no warning message.
 *
 * @see TagInput, Input, Icon
 */

export default class Select extends PureComponent {

  static displayName = 'Select';

  static propTypes = {
    autoComplete: PropTypes.string,
    className: PropTypes.string,
    clearable: PropTypes.bool,
    disabled: PropTypes.bool,
    errorMessage: PropTypes.node, // eslint-disable-line
    iconName: PropTypes.string, // eslint-disable-line
    label: PropTypes.string, // eslint-disable-line
    labelKey: PropTypes.string, // eslint-disable-line
    locked: PropTypes.bool,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    options: PropTypes.array, // eslint-disable-line
    nestedKey: PropTypes.string, // eslint-disable-line
    nestedKeySelectable: PropTypes.string, // eslint-disable-line
    placeholder: PropTypes.string, // eslint-disable-line
    readOnly: PropTypes.bool,
    required: PropTypes.bool, // eslint-disable-line
    showSearch: PropTypes.bool, // eslint-disable-line
    showUnderline: PropTypes.bool, // eslint-disable-line
    sortOptions: PropTypes.bool, // eslint-disable-line
    value: PropTypes.any, // eslint-disable-line
    valueKey: PropTypes.string, // eslint-disable-line
    warningMessage: PropTypes.node,
  };

  static defaultProps = {
    clearable: false,
    labelKey: 'label',
    options: [],
    placeholder: 'Select an option...',
    readOnly: true,
    showUnderline: true,
    value: '',
    valueKey: 'value',
  };

  constructor(props) {
    super(props);
    this.state = {
      searchValue: null,
      filterOptions: !props.readOnly,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.readOnly !== this.props.readOnly) {
      this.setState({ filterOptions: !this.props.readOnly });
    }
  }

  validateInputValue = value => {
    const { clearable, valueKey, options } = this.props;
    if (clearable && (value === '' || (isObject(value) && value[valueKey] === ''))) {
      return true;
    }
    return options.some(option => valueEqualsOption({ option, value, valueKey }))
  }

  onBlur = e => {
    const { readOnly, dropdown, onBlur, onChange } = this.props;
    if (!readOnly && !dropdown) {
      const value = e.target.value;
      const valueIsValid = this.validateInputValue(value);
      if (valueIsValid && onChange) {
        const event = createFakeEvent(value, e, datasetFromProps(this.props));
        onChange(event);
      }
    }
    if (e && e.target && e.target && e.target.getAttribute && e.target.getAttribute('data-keepSearchValue')) {
      this.setState({ filterOptions: !this.props.readOnly });
      e.target.removeAttribute('data-keepSearchValue');
    } else {
      this.setState({
        filterOptions: !this.props.readOnly,
        searchValue: null
      });
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  getFallbackValueOnChange = () => this.props.clearable ? null : this.props.value;

  onChange = e => {
    let { value } = e.target;
    const { readOnly, dropdown, onChange } = this.props;
    if (e.type === 'change' && !dropdown) {
      this.setState({ searchValue: value });
    } else {
      if (!readOnly) {
        const valueIsValid = this.validateInputValue(value);
        if (!dropdown && !valueIsValid) {
          value = this.getFallbackValueOnChange(value)
          e.target.value = value;
        }
        this.setState({ filterOptions: isString(value) && value.length > 0, searchValue: null });
      }
      const event = createFakeEvent(value, e, datasetFromProps(this.props));
      if (onChange) {
        onChange(event);
      }
      this.refInput.onBlur(event);
    }
  };

  onClick = e => {
    const { disabled, locked } = this.props;

    if (!disabled && !locked) {
      this.setState({ filterOptions: false });
      this.refInput.onFocus();
    }

    const { buttonname } = e.target.dataset;
    if (this.refInput?.state?.showOptions && buttonname === 'dropdown') {
      this.refInput.onBlur(e);
    }
    // Hack! Bubbling events causes issues in legacy modules
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopPropagation();
    e.nativeEvent.preventDefault();
    return false;
  };

  onFocus = e => {
    if (!this.props.readOnly) {
      this.setState({ filterOptions: false });
    }
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };

  getOptions = () => {
    return getSearchOptions(this.props.options, this.state.searchValue);
  }

  render() {
    const { className, disabled, info, value, ...rest } = this.props;
    const { filterOptions, searchValue } = this.state;
    return (
      <Input
        {...rest}
        className={cx('core-select', className)}
        disabled={disabled}
        filterOptions={isNil(searchValue) ? false : filterOptions}
        onBlur={this.onBlur}
        onChange={this.onChange}
        onClick={this.onClick}
        onFocus={this.onFocus}
        options={this.getOptions()}
        ref={c => { this.refInput = c; }}
        showClear={this.props.clearable && this.props.value != null && this.props.value !== ''}
        showOptionsOnFocus
        value={isNil(searchValue) ? value : searchValue}
      >
        <Button
          tabIndex="-1"
          className="dropdown-button"
          disabled={disabled}
          iconName="dropdown"
          onClick={this.onClick}
          data-buttonname="dropdown"
        />
      </Input>
    );
  }

}
